import React, { createContext, useState, useContext, useEffect } from 'react';

type Language = 'en' | 'hi';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isHindi: boolean;
}

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
  isHindi: false
});

// Provider component that wraps your application
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to get language from localStorage or use browser's preferred language
    const savedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (savedLanguage && ['en', 'hi'].includes(savedLanguage)) {
      return savedLanguage;
    }
    
    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'hi' ? 'hi' : 'en';
  });

  const isHindi = language === 'hi';

  // Handle language change
  const setLanguage = (lang: Language) => {
    console.log(`Changing language to: ${lang}`);
    setLanguageState(lang);
    localStorage.setItem('preferredLanguage', lang);
    // Update document lang attribute
    document.documentElement.lang = lang;
  };

  // Add this effect to handle URL-based language detection
  useEffect(() => {
    // Handle URL-based language detection
    const path = window.location.pathname;
    if (path.includes('/hindi') && language !== 'hi') {
      setLanguage('hi');
    }
    
    // Listen for navigation events
    const handleLocationChange = () => {
      const newPath = window.location.pathname;
      if (newPath.includes('/hindi') && language !== 'hi') {
        setLanguage('hi');
      } else if (!newPath.includes('/hindi') && language === 'hi') {
        setLanguage('en');
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [language]);

  // Simple translation function
  const translations: Record<string, Record<string, string>> = {
    en: {
      'home': 'Home',
      'search': 'Search',
      'read_more': 'Read More',
      'sign_in': 'Sign In',
      'create_account': 'Create Account',
      'related_articles': 'Related Articles',
      'loading': 'Loading...',
      'share': 'Share',
      'save': 'Save',
      'print': 'Print',
      'more_articles': 'More Articles',
      'back_to_home': 'Back to Home',
      'published_on': 'Published on',
      'by_author': 'by',
      'download_pdf': 'Download PDF',
      'login_with_google': 'Sign in with Google',
      'signup_with_google': 'Sign up with Google',
      'or': 'or',
      'email_address': 'Email Address',
      'password': 'Password',
      'forgot_password': 'Forgot Password?',
      'full_name': 'Full Name',
      'password_requirement': 'Password must be at least 6 characters long',
      'signing_in': 'Signing in...',
      'creating_account': 'Creating account...',
      'dont_have_account': "Don't have an account?",
      'already_have_account': "Already have an account?",
      'create_new_account': 'Create an account',
      'sign_in_instead': 'Sign in',
      // Add more translations as needed
      'search_legal_nest': 'Search Legal Nest',
      'close': 'Close',
      'search_placeholder': 'Search for news, judgments, or articles...',
      // Navigation items
      'nav.top_stories': 'Top Stories',
      'nav.news_updates': 'News Updates',
      'nav.courts': 'Courts',
      'nav.supreme_court': 'Supreme Court',
      'nav.allahabad_high_court': 'Allahabad High Court',
      'nav.bombay_high_court': 'Bombay High Court',
      'nav.delhi_high_court': 'Delhi High Court',
      'nav.karnataka_high_court': 'Karnataka High Court',
      'nav.madras_high_court': 'Madras High Court',
      'nav.rajasthan_high_court': 'Rajasthan High Court',
      'nav.other_high_courts': 'Other High Courts',
      'nav.know_the_law': 'Know The Law',
      'nav.law_schools': 'Law Schools',
      'nav.articles': 'Articles',
      'nav.scholarships': 'Scholarships',
      'nav.courses': 'Courses',
      'nav.admissions': 'Admissions',
      'nav.law_firms': 'Law Firms',
      'nav.deal_news': 'Deal News',
      'nav.events': 'Events',
      'nav.foreign_law_firms': 'Foreign Law Firms',
      'nav.internships': 'Internships',
      'nav.more': 'More',
      'nav.bare_acts': 'Bare Acts',

      // Header
      'user_profile': 'User Profile',
      'premium_subscriber': 'Premium Subscriber',
      'free_account': 'Free Account',
      'my_profile': 'My Profile',
      'saved_articles': 'Saved Articles',
      'logout': 'Logout',
      'close_menu': 'Close Menu',
      'open_menu': 'Open Menu',
      'gift_premium': 'Gift Premium',
      'subscribe_premium': 'Subscribe Premium',

      // Footer
      'advertise_with_us': 'Advertise with Legal Nest',
      'reach_legal_audience': 'Reach India\'s largest audience of legal professionals and law students',
      'learn_more': 'Learn More',
      'footer_description': 'Legal Nest is India\'s leading legal news portal providing updates on judgments, legal developments, law firms, and law schools.',
      'resources': 'Resources',
      'supreme_court': 'Supreme Court',
      'high_courts': 'High Courts',
      'bare_acts': 'Bare Acts',
      'law_firms': 'Law Firms',
      'top_stories': 'Top Stories',
      'company': 'Company',
      'about_us': 'About Us',
      'contact_us': 'Contact Us',
      'privacy_policy': 'Privacy Policy',
      'terms_of_use': 'Terms of Use',
      'subscribe': 'Subscribe',
      'newsletter': 'Newsletter',
      'newsletter_description': 'Subscribe to our newsletter for daily legal updates and news.',
      'your_email_address': 'Your email address',
      'enter_valid_email': 'Please enter a valid email',
      'subscribed_successfully': 'Subscribed successfully!',
      'failed_to_subscribe': 'Failed to subscribe',
      'legal_nest': 'Legal Nest',
      'all_rights_reserved': 'All Rights Reserved'
    },
    hi: {
      'home': 'होम',
      'search': 'खोज',
      'read_more': 'और पढ़ें',
      'sign_in': 'साइन इन करें',
      'create_account': 'खाता बनाएं',
      'related_articles': 'संबंधित लेख',
      'loading': 'लोड हो रहा है...',
      'share': 'शेयर करें',
      'save': 'सहेजें',
      'print': 'प्रिंट करें',
      'more_articles': 'और लेख',
      'back_to_home': 'होम पेज पर वापस जाएं',
      'published_on': 'प्रकाशित',
      'by_author': 'द्वारा',
      'download_pdf': 'पीडीएफ डाउनलोड करें',
      'login_with_google': 'Google से साइन इन करें',
      'signup_with_google': 'Google से साइन अप करें',
      'or': 'या',
      'email_address': 'ईमेल पता',
      'password': 'पासवर्ड',
      'forgot_password': 'पासवर्ड भूल गए?',
      'full_name': 'पूरा नाम',
      'password_requirement': 'पासवर्ड कम से कम 6 अक्षर लंबा होना चाहिए',
      'signing_in': 'साइन इन हो रहा है...',
      'creating_account': 'खाता बन रहा है...',
      'dont_have_account': "खाता नहीं है?",
      'already_have_account': "पहले से खाता है?",
      'create_new_account': 'नया खाता बनाएं',
      'sign_in_instead': 'साइन इन करें',
      // Add more translations as needed
      'search_legal_nest': 'लीगल नेस्ट खोजें',
      'close': 'बंद करें',
      'search_placeholder': 'समाचार, निर्णय, या लेख खोजें...',
      // Navigation items
      'nav.top_stories': 'टॉप स्टोरीज',
      'nav.news_updates': 'समाचार अपडेट',
      'nav.courts': 'अदालतें',
      'nav.supreme_court': 'सुप्रीम कोर्ट',
      'nav.allahabad_high_court': 'इलाहाबाद हाई कोर्ट',
      'nav.bombay_high_court': 'बॉम्बे हाई कोर्ट',
      'nav.delhi_high_court': 'दिल्ली हाई कोर्ट',
      'nav.karnataka_high_court': 'कर्नाटक हाई कोर्ट',
      'nav.madras_high_court': 'मद्रास हाई कोर्ट',
      'nav.rajasthan_high_court': 'राजस्थान हाई कोर्ट',
      'nav.other_high_courts': 'अन्य हाई कोर्ट',
      'nav.know_the_law': 'कानून जानें',
      'nav.law_schools': 'लॉ स्कूल',
      'nav.articles': 'लेख',
      'nav.scholarships': 'छात्रवृत्ति',
      'nav.courses': 'पाठ्यक्रम',
      'nav.admissions': 'प्रवेश',
      'nav.law_firms': 'लॉ फर्म',
      'nav.deal_news': 'डील न्यूज',
      'nav.events': 'कार्यक्रम',
      'nav.foreign_law_firms': 'विदेशी लॉ फर्म',
      'nav.internships': 'इंटर्नशिप',
      'nav.more': 'अधिक',
      'nav.bare_acts': 'कानून संहिता',

      // Header
      'user_profile': 'उपयोगकर्ता प्रोफ़ाइल',
      'premium_subscriber': 'प्रीमियम सदस्य',
      'free_account': 'फ्री अकाउंट',
      'my_profile': 'मेरी प्रोफाइल',
      'saved_articles': 'सहेजे गए लेख',
      'logout': 'लॉगआउट',
      'close_menu': 'मेनू बंद करें',
      'open_menu': 'मेनू खोलें',
      'gift_premium': 'गिफ्ट प्रीमियम',
      'subscribe_premium': 'प्रीमियम सब्सक्राइब करें',

      // Footer
      'advertise_with_us': 'लीगल नेस्ट के साथ विज्ञापन दें',
      'reach_legal_audience': 'भारत के सबसे बड़े कानूनी पेशेवरों और कानून के छात्रों तक पहुंचें',
      'learn_more': 'और जानें',
      'footer_description': 'लीगल नेस्ट भारत का प्रमुख कानूनी समाचार पोर्टल है जो फैसलों, कानूनी विकास, लॉ फर्म और लॉ स्कूलों के अपडेट प्रदान करता है।',
      'resources': 'संसाधन',
      'supreme_court': 'सुप्रीम कोर्ट',
      'high_courts': 'हाई कोर्ट',
      'bare_acts': 'बेयर एक्ट्स',
      'law_firms': 'लॉ फर्म',
      'top_stories': 'टॉप स्टोरीज',
      'company': 'कंपनी',
      'about_us': 'हमारे बारे में',
      'contact_us': 'संपर्क करें',
      'privacy_policy': 'गोपनीयता नीति',
      'terms_of_use': 'उपयोग की शर्तें',
      'subscribe': 'सब्सक्राइब',
      'newsletter': 'न्यूज़लेटर',
      'newsletter_description': 'दैनिक कानूनी अपडेट और समाचारों के लिए हमारे न्यूज़लेटर की सदस्यता लें।',
      'your_email_address': 'आपका ईमेल पता',
      'enter_valid_email': 'कृपया एक वैध ईमेल दर्ज करें',
      'subscribed_successfully': 'सफलतापूर्वक सब्सक्राइब किया गया!',
      'failed_to_subscribe': 'सब्सक्राइब करने में विफल',
      'legal_nest': 'लीगल नेस्ट',
      'all_rights_reserved': 'सर्वाधिकार सुरक्षित'
    }
  };

  // Translation function
  const t = (key: string): string => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  useEffect(() => {
    // Set the document language attribute
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isHindi }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to use the language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Language switcher component
export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  
  return (
    <div className="inline-flex items-center">
      {language === 'hi' ? (
        <button 
          onClick={() => setLanguage('en')} 
          className="px-3 py-1 text-sm rounded bg-white border border-neutral-200 hover:bg-neutral-100 flex items-center"
          aria-label="Switch to English language"
        >
          <svg className="w-4 h-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <span>English</span>
        </button>
      ) : (
        <button 
          onClick={() => setLanguage('hi')} 
          className="px-3 py-1 text-sm rounded bg-white border border-neutral-200 hover:bg-neutral-100 flex items-center"
          aria-label="Switch to Hindi language"
        >
          <svg className="w-4 h-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <span>हिन्दी</span>
        </button>
      )}
    </div>
  );
}

export function CompactLanguageSwitcher() {
  const { language, setLanguage } = useLanguage();
  
  return (
    <button 
      onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
      className="px-2 py-1 text-sm rounded bg-white border border-neutral-200 flex items-center"
      aria-label={language === 'en' ? "Switch to Hindi language" : "Switch to English language"}
    >
      {language === 'en' ? (
        <>
          <span className="mr-1"></span>
          <span className="sr-only md:not-sr-only">हिन्दी</span>
        </>
      ) : (
        <>
          <span className="mr-1"></span>
          <span className="sr-only md:not-sr-only">English</span>
        </>
      )}
    </button>
  );
}